import { useCallback, useMemo, useState } from 'react';
import * as asyncValue from '../redux/asyncValue';

export type AsyncValueWithActions<T> = asyncValue.AsyncValue<T> & {
  empty: () => void;
  setPending: () => void;
  setResolve: (value: T) => void;
  setReject: (errors: string[]) => void;
};

/**
 * Hook which will manage the state of an AsyncValue. This allows you to easily
 * use an AsyncValue within a React component. Non-prescriptive about the use-cases,
 * but this is a better version of our existing
 *
 * @example
 * type UserData = {
 *   id: number;
 *   name: string;
 * };
 *
 * const MyComponent = () => {
 *   const { value, ...actions } = useAsyncValue<UserData>(null);
 *
 *   useOnMount(async () => {
 *     try {
 *       actions.setPending();
 *       const { data } = await get<UserData>('/api/user/me');
 *       actions.setResolve(data);
 *     } catch (error) {
 *       actions.setReject(errorMessagesListFromResponse(error));
 *     }
 *   });
 *   // This is a good case to use LoadingBoundary
 *   return (
 *     <LoadingBoundary
 *       asyncValue={value}
 *       renderLoading={Loader}
 *       renderError={({ errors }) => <Error>{errors.join(', ')}</Error>}
 *       renderEmpty={() => <h1>You need to log in!</h1>}
 *       renderValue={({ value: user }) => <h1>Hello, {user.name}!</h1>}
 *     />
 *   );
 * };
 */
function useAsyncValue<T>(initialValue: T | null = null): AsyncValueWithActions<T> {
  const [asyncValueState, setAsyncValueState] = useState<asyncValue.AsyncValue<T>>(
    initialValue ? asyncValue.resolve(initialValue) : asyncValue.empty(),
  );

  const empty = useCallback(() => setAsyncValueState(asyncValue.empty()), []);

  const setPending = useCallback(() => setAsyncValueState(asyncValue.pending()), []);
  const setResolve = useCallback((value: asyncValue.AsyncValue<T>['value']) => setAsyncValueState(asyncValue.resolve(value as T)), []);
  const setReject = useCallback((errors: asyncValue.AsyncValue<T>['errors']) => setAsyncValueState(asyncValue.reject(errors || ['Something went wrong'])), []);

  return useMemo(() => ({
    ...asyncValueState,
    setPending,
    setResolve,
    setReject,
    empty,
  }), [asyncValueState, setPending, setResolve, setReject, empty]);
}

export default useAsyncValue;
